import { useEffect, useMemo, useRef, useState } from 'react';
import {
  SPHERES,
  SPHERE_NAME_MAX,
  goalStatus,
  isSecondMonth,
  sphereDefaultName,
  balance,
  isDone,
  SKILL_NAME_MAX_NEW,
  monthDays,
  monthKeyOf,
  shiftMonth,
  type DayKey,
  type MonthKey,
  type Skill,
  type SphereId,
  type StateV2,
} from '@superposition/domain';
import { useLoadedState } from '../../lib/store.js';
import { matrixColor } from '../../lib/palette.js';
import { haptic } from '../../lib/telegram.js';
import { useSphereName } from '../../lib/names.js';
import { useT } from '../../i18n/index.js';
import { useLocale } from '../../lib/profile.js';

/**
 * видимая часть экрана над клавиатурой. в telegram на iOS клавиатура не
 * сжимает страницу, поэтому окно добавления держим внутри visualViewport.
 */
function useVisualViewport(active: boolean): { top: number; height: number } | null {
  const [box, setBox] = useState<{ top: number; height: number } | null>(null);
  useEffect(() => {
    const vv = (globalThis as { visualViewport?: VisualViewport }).visualViewport;
    if (!active || !vv) {
      setBox(null);
      return;
    }
    const sync = (): void => setBox({ top: vv.offsetTop, height: vv.height });
    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
    };
  }, [active]);
  return box;
}

function newSkillId(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  const raw = g.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  return `k${raw.replace(/-/g, '').slice(0, 24)}`;
}

/** итог месяца и цель; красный — цель в этом месяце уже не успеть */
function GoalCell({
  skill,
  days,
  today,
  isCurrent,
  onOpen,
}: {
  skill: Skill;
  days: { date: DayKey }[];
  today: DayKey;
  isCurrent: boolean;
  onOpen: () => void;
}): React.ReactNode {
  const t = useT();
  const done = days.filter((d) => isDone(skill, d.date)).length;
  const lost = isCurrent && skill.goal ? goalStatus(skill, today).impossible : false;
  return (
    <button
      type="button"
      className={`mx-goal${lost ? ' lost' : ''}${skill.goal ? '' : ' empty'}`}
      aria-label={t.matrix.goalAria({ skill: skill.name, done, goal: skill.goal ?? null, lost })}
      onClick={onOpen}
    >
      <b>{done}</b>
      <span>/{skill.goal ?? '—'}</span>
    </button>
  );
}

export function MatrixScreen(): React.ReactNode {
  const { state, store } = useLoadedState();
  const t = useT();
  const locale = useLocale();
  const [month, setMonth] = useState<MonthKey>(() => monthKeyOf(store.today));
  // сфера, в которую сейчас добавляем навык; null — окно закрыто
  const [adding, setAdding] = useState<SphereId | null>(null);
  const [draftName, setDraftName] = useState('');
  const [pendingDelete, setPendingDelete] = useState<Skill | null>(null);
  // переименование: навык или сфера; id сферы не меняется, меняется только подпись
  const [renaming, setRenaming] = useState<{ kind: 'skill'; skill: Skill } | { kind: 'sphere'; id: SphereId } | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const sphereName = useSphereName();
  // со второго месяца справа столбец «итог / цель»
  const goalsOn = isSecondMonth(state, store.today);
  const [goalFor, setGoalFor] = useState<Skill | null>(null);
  const [goalDraft, setGoalDraft] = useState('');
  const openGoal = (k: Skill): void => {
    setGoalDraft(k.goal ? String(k.goal) : '');
    setGoalFor(k);
  };
  const saveGoal = (value: number | null): void => {
    if (!goalFor) return;
    haptic('light');
    const id = goalFor.id;
    store.update((prev) => ({
      ...prev,
      skills: prev.skills.map((k) => {
        if (k.id !== id) return k;
        const { goal: _old, ...rest } = k;
        return value ? { ...rest, goal: value } : rest;
      }),
    }));
    setGoalFor(null);
  };
  const scrollRef = useRef<HTMLDivElement>(null);

  const days = useMemo(() => monthDays(month, locale), [month, locale]);
  const isCurrentMonth = month === monthKeyOf(store.today);

  // автопрокрутка к сегодня при открытии текущего месяца
  useEffect(() => {
    if (!isCurrentMonth) return;
    const el = scrollRef.current;
    if (!el) return;
    const idx = days.findIndex((d) => d.date === store.today);
    if (idx < 0) return;
    const colWidth = 35; // 30px + 5px gap
    // сегодня виден целиком правее липкой колонки названий, слева — три прошлых дня
    el.scrollLeft = Math.max(0, (idx - 3) * colWidth);
  }, [isCurrentMonth, days, store.today, month]);

  const doneToday = state.skills.filter((k) => isDone(k, store.today)).length;
  // сколько навыков прокачано за каждый день и индекс баланса зафиксированного дня
  const doneByDay = useMemo(
    () => new Map(days.map((d) => [d.date, state.skills.filter((k) => isDone(k, d.date)).length])),
    [days, state.skills],
  );
  const indexByDay = useMemo(
    () => new Map(state.history.map((h) => [h.date, balance(h.values)])),
    [state.history],
  );

  // отметка — это 1, её снятие — 0. у навыков из старой версии с часами
  // отметкой считаются любые часы, и снимаются они вместе с часами
  const toggle = (skillId: string, date: DayKey): void => {
    haptic('light');
    store.update((prev) => ({
      ...prev,
      skills: prev.skills.map((k) => {
        if (k.id !== skillId) return k;
        const done = { ...k.done };
        const hours = { ...k.hours };
        if (isDone(k, date)) {
          delete done[date];
          delete hours[date];
        } else {
          done[date] = true;
        }
        return { ...k, done, hours };
      }),
    }));
  };

  const openAdd = (sphere: SphereId): void => {
    setDraftName('');
    setAdding(sphere);
  };

  const addSkill = (rawName: string): void => {
    const name = rawName.trim().toLowerCase().slice(0, SKILL_NAME_MAX_NEW);
    if (!name || !adding) return;
    haptic('medium');
    store.update((prev: StateV2) => ({
      ...prev,
      skills: [
        ...prev.skills,
        { id: newSkillId(), sphere: adding, name, type: 'check', done: {}, hours: {} },
      ],
    }));
    setDraftName('');
    setAdding(null);
  };

  const openRenameSkill = (skill: Skill): void => {
    setRenameDraft(skill.name);
    setRenaming({ kind: 'skill', skill });
  };

  const openRenameSphere = (id: SphereId): void => {
    setRenameDraft(sphereName(id));
    setRenaming({ kind: 'sphere', id });
  };

  const saveRename = (): void => {
    if (!renaming) return;
    const name = renameDraft.trim().replace(/\s+/g, ' ');
    if (renaming.kind === 'skill') {
      const next = name.toLowerCase().slice(0, SKILL_NAME_MAX_NEW);
      if (next && next !== renaming.skill.name) {
        haptic('light');
        store.update((prev) => ({
          ...prev,
          skills: prev.skills.map((k) => (k.id === renaming.skill.id ? { ...k, name: next } : k)),
        }));
      }
    } else {
      const next = name.toLowerCase().slice(0, SPHERE_NAME_MAX);
      const id = renaming.id;
      haptic('light');
      store.update((prev) => {
        const names = { ...(prev.sphereNames ?? {}) };
        // пустое или стандартное название — значит вернуть по умолчанию
        if (!next || next === sphereDefaultName(id, locale)) delete names[id];
        else names[id] = next;
        const { sphereNames: _old, ...rest } = prev;
        return Object.keys(names).length ? { ...rest, sphereNames: names } : rest;
      });
    }
    setRenaming(null);
  };

  const removeSkill = (skill: Skill): void => {
    store.update((prev) => ({ ...prev, skills: prev.skills.filter((k) => k.id !== skill.id) }));
    setPendingDelete(null);
  };

  const have = new Set(
    state.skills.filter((k) => k.sphere === adding).map((k) => k.name.toLowerCase()),
  );
  const suggestions = adding ? (t.matrix.suggest[adding] ?? []).filter((x) => !have.has(x.toLowerCase())) : [];
  const addingSphere = adding ? SPHERES.find((s) => s.id === adding) : undefined;
  const viewport = useVisualViewport(adding !== null || renaming !== null || goalFor !== null);

  return (
    <section className="fade" aria-labelledby="matrix-title">
      <h2 className="screen-t" id="matrix-title">
        {t.matrix.title}
      </h2>

      <div className="mx-top">
        <div className="cnt">
          {t.matrix.today} <b>{doneToday}</b> / {state.skills.length}
        </div>
      </div>

      <div className="mx-month">
        <button type="button" aria-label={t.matrix.prevMonth} onClick={() => setMonth((m) => shiftMonth(m, -1))}>
          ‹
        </button>
        <span className="label">{t.common.formatMonth(month)}</span>
        <button type="button" aria-label={t.matrix.nextMonth} onClick={() => setMonth((m) => shiftMonth(m, 1))}>
          ›
        </button>
      </div>

      <div className="mx-scroll" ref={scrollRef}>
        <div className={`mx${goalsOn ? ' goals' : ''}`} style={{ ['--n' as string]: days.length }}>
          <div className="mx-row">
            <div className="mx-corner" />
            {days.map((d) => (
              <div key={d.date} className={`mx-dayh${d.date === store.today ? ' today' : ''}`}>
                <span className="wd">{d.wd}</span>
                <span className="num">{d.day}</span>
              </div>
            ))}
            {goalsOn ? (
              <div className="mx-goal-h">
                {t.matrix.goalHeadTotal}
                <br />
                {t.matrix.goalHeadGoal}
              </div>
            ) : null}
          </div>

          {SPHERES.map((s) => {
            const color = matrixColor(s.id);
            const skills = state.skills.filter((k) => k.sphere === s.id);
            return (
              <div key={s.id} style={{ display: 'contents' }}>
                <div className="mx-row">
                  <div className="mx-grp-c" style={{ color: color.t }}>
                    <span className="tag" style={{ background: color.c }} />
                    <button
                      type="button"
                      className="mx-sph-name"
                      aria-label={t.matrix.renameSphereAria({ sphere: sphereName(s.id) })}
                      onClick={() => openRenameSphere(s.id)}
                    >
                      {sphereName(s.id)}
                    </button>
                    <button
                      type="button"
                      className="mx-plus"
                      aria-label={t.matrix.addSkillAria({ sphere: sphereName(s.id) })}
                      onClick={() => openAdd(s.id)}
                    >
                      +
                    </button>
                  </div>
                </div>
                {skills.length === 0 ? (
                  <div className="mx-row">
                    <button type="button" className="mx-empty" onClick={() => openAdd(s.id)}>
                      {t.matrix.addSkillEmpty}
                    </button>
                  </div>
                ) : null}
                {skills.map((k) => (
                  <div className="mx-row" key={k.id}>
                    <div className="mx-name">
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: 2,
                          background: color.c,
                          flex: 'none',
                        }}
                      />
                      <button
                        type="button"
                        className="lbl"
                        aria-label={t.matrix.renameSkillAria({ skill: k.name })}
                        onClick={() => openRenameSkill(k)}
                      >
                        {k.name}
                      </button>
                      <button
                        type="button"
                        className="del"
                        aria-label={t.matrix.deleteSkillAria({ skill: k.name })}
                        onClick={() => setPendingDelete(k)}
                      >
                        ×
                      </button>
                    </div>
                    {days.map((d) => {
                      const future = d.date > store.today;
                      const on = isDone(k, d.date);
                      // прошедший день без отметки — осознанный 0, сегодня и будущее пустые
                      const bit = on ? '1' : d.date < store.today ? '0' : '';
                      return (
                        <button
                          type="button"
                          key={d.date}
                          className={`mx-c${on ? ' on' : ''}${d.date === store.today ? ' today' : ''}${future ? ' future' : ''}`}
                          style={on ? { background: color.c, borderColor: color.c } : undefined}
                          aria-pressed={on}
                          aria-label={`${k.name}, ${d.day}: ${on ? '1' : '0'}`}
                          onClick={() => toggle(k.id, d.date)}
                        >
                          <span className="bit">{bit}</span>
                        </button>
                      );
                    })}
                    {goalsOn ? <GoalCell skill={k} days={days} today={store.today} isCurrent={isCurrentMonth} onOpen={() => openGoal(k)} /> : null}
                  </div>
                ))}
              </div>
            );
          })}

          {state.skills.length ? (
            <>
              <div className="mx-row mx-sum">
                <div className="mx-name">
                  <span className="lbl">{t.matrix.skillsPerDay}</span>
                </div>
                {days.map((d) => (
                  <div key={d.date} className={`mx-t${d.date === store.today ? ' today' : ''}`}>
                    {d.date <= store.today ? (doneByDay.get(d.date) ?? 0) : ''}
                  </div>
                ))}
              </div>
              <div className="mx-row">
                <div className="mx-name">
                  <span className="lbl">{t.matrix.balancePerDay}</span>
                </div>
                {days.map((d) => (
                  <div key={d.date} className={`mx-t idx${d.date === store.today ? ' today' : ''}`}>
                    {indexByDay.get(d.date) ?? ''}
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </div>
      {state.skills.length ? (
        <p className="mx-note">{t.matrix.note}</p>
      ) : null}

      {adding && addingSphere ? (
        <div
          className="sheet sheet-top"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-title"
          style={viewport ? { top: viewport.top, height: viewport.height, bottom: 'auto' } : undefined}
          onClick={(e) => {
            if (e.target === e.currentTarget) setAdding(null);
          }}
        >
          <div className="sheet-in">
            <h3 id="add-title" style={{ color: matrixColor(adding).t }}>
              <span className="tag" style={{ background: matrixColor(adding).c }} />
              {t.matrix.newSkill({ sphere: sphereName(addingSphere.id) })}
            </h3>
            <div className="add-row">
              <label className="visually-hidden" htmlFor="mx-input">
                {t.matrix.skillNameLabel}
              </label>
              <input
                id="mx-input"
                value={draftName}
                placeholder={t.matrix.skillPlaceholder}
                maxLength={SKILL_NAME_MAX_NEW}
                autoComplete="off"
                enterKeyHint="done"
                autoFocus
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addSkill(draftName);
                  if (e.key === 'Escape') setAdding(null);
                }}
              />
              <button type="button" onClick={() => addSkill(draftName)}>
                {t.matrix.add}
              </button>
            </div>
            {suggestions.length ? (
              <div className="suggest" style={{ marginTop: 12, marginBottom: 0 }}>
                {suggestions.map((x) => (
                  <button type="button" key={x} className="sg" onClick={() => addSkill(x)}>
                    + {x}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="sheet-actions">
              <button type="button" onClick={() => setAdding(null)}>
                {t.common.cancel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {goalFor ? (
        <div
          className="sheet sheet-top"
          role="dialog"
          aria-modal="true"
          aria-labelledby="goal-title"
          style={viewport ? { top: viewport.top, height: viewport.height, bottom: 'auto' } : undefined}
          onClick={(e) => {
            if (e.target === e.currentTarget) setGoalFor(null);
          }}
        >
          <div className="sheet-in">
            <h3 id="goal-title">{t.matrix.goalTitle({ skill: goalFor.name })}</h3>
            <p className="rename-note">{t.matrix.goalQuestion}</p>
            <div className="add-row">
              <label className="visually-hidden" htmlFor="goal-input">
                {t.matrix.goalInputLabel}
              </label>
              <input
                id="goal-input"
                type="number"
                inputMode="numeric"
                min={1}
                max={31}
                value={goalDraft}
                autoFocus
                onChange={(e) => setGoalDraft(e.target.value.replace(/\D/g, '').slice(0, 2))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const v = Number(goalDraft);
                    if (v >= 1 && v <= 31) saveGoal(v);
                  }
                  if (e.key === 'Escape') setGoalFor(null);
                }}
              />
              <button
                type="button"
                disabled={!(Number(goalDraft) >= 1 && Number(goalDraft) <= 31)}
                onClick={() => saveGoal(Number(goalDraft))}
              >
                {t.common.save}
              </button>
            </div>
            <p className="rename-note">{t.matrix.goalNote}</p>
            <div className="sheet-actions">
              {goalFor.goal ? (
                <button type="button" onClick={() => saveGoal(null)}>
                  {t.matrix.removeGoal}
                </button>
              ) : null}
              <button type="button" onClick={() => setGoalFor(null)}>
                {t.common.cancel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {renaming ? (
        <div
          className="sheet sheet-top"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rename-title"
          style={viewport ? { top: viewport.top, height: viewport.height, bottom: 'auto' } : undefined}
          onClick={(e) => {
            if (e.target === e.currentTarget) setRenaming(null);
          }}
        >
          <div className="sheet-in">
            <h3 id="rename-title">
              {renaming.kind === 'skill' ? t.matrix.renameSkill : t.matrix.renameSphere}
            </h3>
            <div className="add-row">
              <label className="visually-hidden" htmlFor="rename-input">
                {t.matrix.newNameLabel}
              </label>
              <input
                id="rename-input"
                value={renameDraft}
                maxLength={renaming.kind === 'skill' ? SKILL_NAME_MAX_NEW : SPHERE_NAME_MAX}
                autoComplete="off"
                enterKeyHint="done"
                autoFocus
                onChange={(e) => setRenameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveRename();
                  if (e.key === 'Escape') setRenaming(null);
                }}
              />
              <button type="button" onClick={saveRename}>
                {t.common.save}
              </button>
            </div>
            <p className="rename-note">
              {renaming.kind === 'skill'
                ? t.matrix.renameSkillNote
                : t.matrix.renameSphereNote({ standard: sphereDefaultName(renaming.id, locale) })}
            </p>
            <div className="sheet-actions">
              {renaming.kind === 'sphere' && sphereName(renaming.id) !== sphereDefaultName(renaming.id, locale) ? (
                <button
                  type="button"
                  onClick={() => {
                    setRenameDraft('');
                    const id = renaming.id;
                    store.update((prev) => {
                      const names = { ...(prev.sphereNames ?? {}) };
                      delete names[id];
                      const { sphereNames: _old, ...rest } = prev;
                      return Object.keys(names).length ? { ...rest, sphereNames: names } : rest;
                    });
                    setRenaming(null);
                  }}
                >
                  {t.matrix.restoreName({ standard: sphereDefaultName(renaming.id, locale) })}
                </button>
              ) : null}
              {renaming.kind === 'skill' ? (
                <button
                  type="button"
                  onClick={() => {
                    setPendingDelete(renaming.skill);
                    setRenaming(null);
                  }}
                >
                  {t.matrix.deleteSkill}
                </button>
              ) : null}
              <button type="button" onClick={() => setRenaming(null)}>
                {t.common.cancel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pendingDelete ? (
        <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="del-title">
          <div className="sheet-in">
            <h3 id="del-title">{t.matrix.deleteTitle({ skill: pendingDelete.name })}</h3>
            <p>
              {t.matrix.deleteText({
                marks: new Set([...Object.keys(pendingDelete.done), ...Object.keys(pendingDelete.hours)]).size,
              })}
            </p>
            <div className="sheet-actions">
              <button type="button" onClick={() => setPendingDelete(null)}>
                {t.common.cancel}
              </button>
              <button type="button" className="primary" onClick={() => removeSkill(pendingDelete)}>
                {t.matrix.delete}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
